import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from 'store';

import logoImg from 'img/logos/logo.svg';

import type { TargetUser } from 'common/accountActivationAndRecovery/types';
import { isTargetUser } from 'common/accountActivationAndRecovery/types';
import { CIAnalytics } from 'common/analytics/analytics.provider';
import { AuthActions } from 'common/auth/actions';
import { PasswordUpdated } from 'common/viewcomp/forms/password_updated';

import ChangeAppLanguage from '../../../auth/change_language';
import { ActivationStep } from '../ActivationStep';
import ActivationCodeForm from '../form/activation_code';
import CreatePasswordForm from '../form/create_pass';
import { SecurityQuestionUserActivation } from '../form/SecurityQuestionUserActivation';
import '../user_activation.page.scss';

import { useAdvanceAfterPasswordCreated, useResumeActivation } from './activationStepProgression';

export interface UserActivationFlowProps {
    targetUser: TargetUser | null;
    isPasswordReset: boolean;
    activationCodeReadOnly: boolean;
    hideLogo: boolean;
    showStepper: boolean;
    showSuccessView: boolean;
    onComplete: () => void;
    prefilledActivationCode?: string;
    onBackToLogin?: () => void;
}

export const UserActivationFlow = ({
    targetUser,
    isPasswordReset,
    activationCodeReadOnly,
    hideLogo,
    showStepper,
    showSuccessView,
    onComplete,
    prefilledActivationCode,
    onBackToLogin,
}: UserActivationFlowProps): ReactElement => {
    const { t } = useTranslation('user');
    const dispatch = useDispatch();
    const activeUser = useSelector((state: RootState) => state.auth.activeUser);

    const [activeStep, setActiveStep] = useState<ActivationStep>(ActivationStep.Code);

    const activatedUser = isTargetUser(activeUser, targetUser) ? activeUser : null;

    function finish(): void {
        if (showSuccessView) {
            setActiveStep(ActivationStep.Success);
        } else {
            onComplete();
        }
    }

    useResumeActivation({
        activatedUser,
        targetUser,
        onFinish: finish,
        onNeedsPassword: (): void => setActiveStep(ActivationStep.Password),
    });

    useEffect(() => {
        return (): void => {
            dispatch(AuthActions.forgotCredentialsCleared());
        };
    }, [dispatch]);

    useEffect(() => {
        if (activatedUser) {
            CIAnalytics.getInstance().identifyUser(activatedUser);
        }
    }, [activatedUser]);

    useAdvanceAfterPasswordCreated({
        isOnPasswordStep: activeStep === ActivationStep.Password,
        activatedUser,
        isPasswordReset,
        onFinish: finish,
        onAdvanceToSecurity: (): void => setActiveStep(ActivationStep.Security),
    });

    function renderActivationForm(): ReactElement {
        switch (activeStep) {
            case ActivationStep.Code:
                return (
                    <ActivationCodeForm
                        activeStep={activeStep}
                        onCompleteStep={(): void => setActiveStep(ActivationStep.Password)}
                        onBackToLogin={onBackToLogin}
                        targetUser={targetUser}
                        isPasswordReset={isPasswordReset}
                        prefilledActivationCode={prefilledActivationCode}
                        activationCodeReadOnly={activationCodeReadOnly}
                        showStepper={showStepper}
                    />
                );
            case ActivationStep.Password:
                return (
                    <CreatePasswordForm
                        activeStep={activeStep}
                        onBackToLogin={onBackToLogin}
                        showStepper={showStepper}
                    />
                );
            case ActivationStep.Security:
                return (
                    <SecurityQuestionUserActivation
                        activeStep={activeStep}
                        onCompleteStep={finish}
                        onBackToLogin={onBackToLogin}
                    />
                );
            case ActivationStep.Success:
                return (
                    <div id="password_updated_wrapper">
                        <PasswordUpdated />
                    </div>
                );
            default:
                return <></>;
        }
    }

    return (
        <>
            {activeStep === ActivationStep.Code && <ChangeAppLanguage />}
            <div id="activation_page">
                <div className="activation_container" role="main">
                    {!hideLogo && (
                        <div className="lunexis_logo">
                            <img alt={t('general.clinical_ink_logo')} src={logoImg} />
                        </div>
                    )}

                    {renderActivationForm()}
                </div>
            </div>
        </>
    );
};
